/**
 * IndexedDB storage engine for offline manga chapters
 * Stores high-res manga images as binary Blobs with zero CORS issues
 */

export interface OfflineChapterSummary {
  id: string; // `${mangaId}_${chapterNum}`
  mangaId: string;
  mangaTitle: string;
  chapterNum: number;
  sourceName: string;
  sourceUrl: string;
  downloadedAt: string;
  imageCount: number;
  sizeBytes: number;
}

export interface OfflineChapter extends OfflineChapterSummary {
  images: Blob[]; // Binary image blobs
}

const DB_NAME = 'MangaOfflineDB_v1';
const DB_VERSION = 1;
const STORE_NAME = 'chapters';

let dbInstance: IDBDatabase | null = null;

export function initDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('เบราว์เซอร์นี้ไม่รองรับ IndexedDB'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('by_mangaId', 'mangaId', { unique: false });
        store.createIndex('by_chapterNum', 'chapterNum', { unique: false });
        store.createIndex('by_downloadedAt', 'downloadedAt', { unique: false });
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onerror = () => {
      reject(request.error || new Error('ไม่สามารถเปิด IndexedDB ได้'));
    };
  });
}

/**
 * Save downloaded chapter to IndexedDB
 */
export async function saveChapter(chapter: OfflineChapter): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(chapter);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error || new Error('บันทึกตอนลงฐานข้อมูลล้มเหลว'));
  });
}

/**
 * Get a single chapter with all image Blobs
 */
export async function getChapter(
  mangaId: string,
  chapterNum: number
): Promise<OfflineChapter | null> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const id = `${mangaId}_${chapterNum}`;
    const req = store.get(id);

    req.onsuccess = () => {
      resolve(req.result || null);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Get summary of all downloaded chapters for a specific manga (without heavy Blobs)
 */
export async function getDownloadedChapters(
  mangaId: string
): Promise<OfflineChapterSummary[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('by_mangaId');
    const req = index.getAll(mangaId);

    req.onsuccess = () => {
      const results: OfflineChapter[] = req.result || [];
      // Strip blobs to keep memory lightweight
      const summaries: OfflineChapterSummary[] = results.map((c) => ({
        id: c.id,
        mangaId: c.mangaId,
        mangaTitle: c.mangaTitle,
        chapterNum: c.chapterNum,
        sourceName: c.sourceName,
        sourceUrl: c.sourceUrl,
        downloadedAt: c.downloadedAt,
        imageCount: c.imageCount || c.images?.length || 0,
        sizeBytes: c.sizeBytes || 0,
      }));
      // Sort chapters ascending
      summaries.sort((a, b) => a.chapterNum - b.chapterNum);
      resolve(summaries);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Downloaded chapter numbers for every manga, read from keys only so no image Blobs load
 */
export async function getAllDownloadedChapterNums(): Promise<Map<string, number[]>> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readonly');
    const req = tx.objectStore(STORE_NAME).getAllKeys();

    req.onsuccess = () => {
      const byManga = new Map<string, number[]>();
      for (const key of req.result as string[]) {
        // id is `${mangaId}_${chapterNum}`; manga ids may contain "_" themselves
        const cut = String(key).lastIndexOf('_');
        if (cut <= 0) continue;
        const mangaId = String(key).slice(0, cut);
        const chapterNum = Number(String(key).slice(cut + 1));
        if (!Number.isFinite(chapterNum)) continue;
        const list = byManga.get(mangaId) || [];
        list.push(chapterNum);
        byManga.set(mangaId, list);
      }
      byManga.forEach((list) => list.sort((a, b) => a - b));
      resolve(byManga);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Delete a specific chapter from IndexedDB
 */
export async function deleteChapter(mangaId: string, chapterNum: number): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const id = `${mangaId}_${chapterNum}`;
    const req = store.delete(id);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Delete all chapters for a manga
 */
export async function deleteAllChaptersForManga(mangaId: string): Promise<void> {
  const db = await initDB();
  const chapters = await getDownloadedChapters(mangaId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const c of chapters) {
      store.delete(c.id);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Format bytes to readable string (e.g. 15.4 MB)
 */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(1)} ${units[i]}`;
}

/**
 * Calculate total offline storage used
 */
export async function getMangaOfflineStorageSize(
  mangaId?: string
): Promise<{ totalBytes: number; formattedSize: string; chapterCount: number }> {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = mangaId ? store.index('by_mangaId').getAll(mangaId) : store.getAll();

      req.onsuccess = () => {
        const results: OfflineChapter[] = req.result || [];
        let totalBytes = 0;
        for (const c of results) {
          totalBytes += c.sizeBytes || 0;
        }
        resolve({
          totalBytes,
          formattedSize: formatBytes(totalBytes),
          chapterCount: results.length,
        });
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return { totalBytes: 0, formattedSize: '0 B', chapterCount: 0 };
  }
}
